import { useEffect, useMemo, useRef, useState } from "react";
import {
  useAdminLogsInfinite,
  useAdminLogFilters,
  useAdminRecoverLog,
  useAdminRecoverStuckLogs,
  useAdminDeleteLog,
  useAdminFailLog,
} from "../hooks/useApi";
import AdminLogsTable from "../components/AdminLogsTable";
import { formatNumber } from "../utils/format";
import { useAdminMe } from "../hooks/useApi";
import "../admin-theme.css";

const STATUS_LABELS: Record<string, string> = {
  pending: "در انتظار",
  forwarded: "ارسال‌شده",
  completed: "تکمیل‌شده",
  failed: "ناموفق",
  recovery_needed: "نیاز به بازیابی",
};

export default function AdminLogs() {
  const { data: adminMe } = useAdminMe();
  const permissions = (adminMe?.permissions as string[]) || [];
  const canRecover = permissions.includes("logs.manage");

  const [modelInput, setModelInput] = useState("");
  const [modelSearch, setModelSearch] = useState("");
  const [userInput, setUserInput] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [endpoint, setEndpoint] = useState("");
  const [status, setStatus] = useState("");
  const { data: filters } = useAdminLogFilters();
  const filterOptions = filters as { endpoints: string[]; statuses: string[] } | undefined;

  const recoverMutation = useAdminRecoverLog();
  const bulkMutation = useAdminRecoverStuckLogs();
  const deleteMutation = useAdminDeleteLog();
  const failMutation = useAdminFailLog();
  const [recoverModalLog, setRecoverModalLog] = useState<Record<string, unknown> | null>(null);
  const [genIdInput, setGenIdInput] = useState("");
  const [selectedCandidate, setSelectedCandidate] = useState<string | null>(null);
  const [recoveringId, setRecoveringId] = useState<number | null>(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmFail, setConfirmFail] = useState(false);

  // Debounce model search input
  useEffect(() => {
    const timer = setTimeout(() => setModelSearch(modelInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [modelInput]);

  // Debounce user search input
  useEffect(() => {
    const timer = setTimeout(() => setUserSearch(userInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [userInput]);

  const filterParams = useMemo(() => {
    const params: Record<string, unknown> = {};
    if (modelSearch) params.model = modelSearch;
    if (userSearch) params.user_search = userSearch;
    if (endpoint) params.endpoint = endpoint;
    if (status) params.status_filter = status;
    return params;
  }, [modelSearch, userSearch, endpoint, status]);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useAdminLogsInfinite(filterParams);

  const sentinelRef = useRef<HTMLDivElement>(null);

  // Infinite scroll: load next page when the sentinel enters the viewport
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "400px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const logs = useMemo(
    () => (data?.pages ?? []).flat() as Record<string, unknown>[],
    [data]
  );

  const openRecover = (log: Record<string, unknown>) => {
    setGenIdInput((log.openrouter_generation_id as string) ?? "");
    setSelectedCandidate(null);
    setRecoverModalLog(log);
    setConfirmDelete(false);
    setConfirmFail(false);
  };

  const candidates = useMemo(() => {
    const raw = recoverModalLog?.recovery_candidates;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.filter((c): c is { generation_id: string } & Record<string, unknown> =>
      Boolean(c && typeof c === "object" && typeof (c as { generation_id?: unknown }).generation_id === "string")
    );
  }, [recoverModalLog]);

  const pickCandidate = (genId: string) => {
    setSelectedCandidate(genId);
    setGenIdInput(genId);
  };

  const asNumber = (v: unknown): number | undefined => {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const asString = (v: unknown): string | undefined =>
    typeof v === "string" && v ? v : undefined;

  // Format a candidate's generation time for display. Two sources exist:
  // - `created_at_exact` / enriched `created_at`: second-precision upstream
  //   timestamp from OpenRouter (ISO-8601, UTC).
  // - unenriched `created_at`: analytics minute bucket, space-separated UTC.
  // Rendered in Jalali date (fa-IR locale) at Tehran time (Asia/Tehran).
  const formatCandidateTime = (cand: Record<string, unknown>): string => {
    const iso = asString(cand.created_at_exact) || asString(cand.created_at);
    if (!iso) return "—";
    const norm =
      typeof iso === "string" && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}$/.test(iso)
        ? iso.replace(" ", "T") + "Z"
        : iso;
    const date = new Date(norm);
    if (isNaN(date.getTime())) return iso;
    return (
      date.toLocaleString("fa-IR", {
        timeZone: "Asia/Tehran",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }) + " به وقت تهران"
    );
  };

  const confirmManualRecover = async () => {
    const log = recoverModalLog;
    if (!log) return;
    const genId = genIdInput.trim() || (log.openrouter_generation_id as string ?? "");
    if (!genId) return;
    const id = log.id as number;
    setRecoveringId(id);
    try {
      await recoverMutation.mutateAsync({ id, openrouter_generation_id: genId });
      setRecoverModalLog(null);
      setSelectedCandidate(null);
    } finally {
      setRecoveringId(null);
    }
  };

  const runBulk = async () => {
    setConfirmBulk(false);
    await bulkMutation.mutateAsync(100);
  };

  const confirmDeleteLog = async () => {
    const log = recoverModalLog;
    if (!log) return;
    const id = log.id as number;
    setConfirmDelete(false);
    setRecoveringId(id);
    try {
      await deleteMutation.mutateAsync(id);
      setRecoverModalLog(null);
      setSelectedCandidate(null);
    } finally {
      setRecoveringId(null);
    }
  };

  const confirmFailLog = async () => {
    const log = recoverModalLog;
    if (!log) return;
    const id = log.id as number;
    setConfirmFail(false);
    setRecoveringId(id);
    try {
      await failMutation.mutateAsync(id);
      setRecoverModalLog(null);
      setSelectedCandidate(null);
    } finally {
      setRecoveringId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: "var(--admin-text-primary)" }}>لاگ‌ها</h1>
        <p className="text-sm mt-1" style={{ color: "var(--admin-text-secondary)" }}>گزارش دقیق تمام درخواست‌های عبوری از گیت‌وی</p>
      </div>

      {/* Logs Table */}
      <div className="admin-card overflow-hidden">
        <div className="admin-card-header">
          <h2 className="admin-card-title">جدول لاگ‌ها</h2>
          <div className="flex items-center gap-2">
            <span className="admin-badge admin-badge-info">{logs ? formatNumber(logs.length) : "۰"} رکورد</span>
            {canRecover && (confirmBulk ? (
              <span className="flex items-center gap-1.5">
                <span className="text-xs" style={{ color: "var(--admin-text-secondary)" }}>
                  بازیابی همه لاگ‌های گیر کرده؟
                </span>
                <button
                  type="button"
                  className="admin-btn admin-btn-primary admin-btn-sm"
                  disabled={bulkMutation.isPending}
                  onClick={runBulk}
                >
                  بله
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary admin-btn-sm"
                  disabled={bulkMutation.isPending}
                  onClick={() => setConfirmBulk(false)}
                >
                  انصراف
                </button>
              </span>
            ) : (
              <button
                type="button"
                className="admin-btn admin-btn-primary admin-btn-sm"
                disabled={bulkMutation.isPending}
                onClick={() => setConfirmBulk(true)}
              >
                {bulkMutation.isPending ? "در حال بازیابی…" : "بازیابی همه"}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 border-b" style={{ borderColor: "var(--admin-border)" }}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                جستجوی مدل
              </label>
              <input
                type="text"
                className="admin-input"
                placeholder="جستجو در نام مدل..."
                value={modelInput}
                onChange={(e) => setModelInput(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                جستجوی کاربر
              </label>
              <input
                type="text"
                className="admin-input"
                placeholder="ایمیل، نام کاربری یا شماره موبایل..."
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                فیلتر Endpoint
              </label>
              <select
                className="admin-input ltr text-left"
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
              >
                <option value="">همه</option>
                {(filterOptions?.endpoints || []).map((ep) => (
                  <option key={ep} value={ep}>{ep}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                فیلتر وضعیت
              </label>
              <select
                className="admin-input"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">همه</option>
                {(filterOptions?.statuses || []).map((st) => (
                  <option key={st} value={st}>{STATUS_LABELS[st] || st}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : !logs.length ? (
          <div className="flex flex-col items-center justify-center py-16" style={{ color: "var(--admin-text-tertiary)" }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="mb-3 opacity-50" stroke="currentColor" strokeWidth="1.5">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <p className="text-sm">هیچ لاگی یافت نشد</p>
          </div>
        ) : (
          <>
            <AdminLogsTable logs={logs} onRecover={openRecover} recoveringId={recoveringId} canRecover={canRecover} />
            {/* Infinite scroll sentinel + loading footer */}
            <div ref={sentinelRef} className="flex items-center justify-center py-6">
              {isFetchingNextPage ? (
                <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24" fill="none" style={{ color: "var(--admin-primary)" }}>
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : !hasNextPage ? (
                <span className="text-xs" style={{ color: "var(--admin-text-tertiary)" }}>پایان لیست</span>
              ) : null}
            </div>
          </>
        )}
      </div>

      {/* Manual generation-id modal */}
      {recoverModalLog ? (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
          <div className="admin-card max-w-md w-full" style={{ margin: "0 1rem" }}>
            <div className="admin-card-header">
              <h2 className="admin-card-title">بازیابی دستی لاگ</h2>
            </div>
            <div className="p-4">
              {candidates.length > 0 ? (
                <>
                  <p className="text-sm mb-3" style={{ color: "var(--admin-text-secondary)" }}>
                    این لاگ شناسه generation ندارد، اما سرویس کشف، نامزدهای زیر را از فعالیت OpenRouter در زمان وقوع درخواست یافته است. یکی را انتخاب کنید:
                  </p>
                  <div className="space-y-2 mb-3 max-h-[50vh] overflow-y-auto">
                    {candidates.map((cand) => (
                      <button
                        key={cand.generation_id}
                        type="button"
                        onClick={() => pickCandidate(cand.generation_id)}
                        className={`w-full text-left admin-card p-3 transition ${
                          selectedCandidate === cand.generation_id
                            ? "outline outline-2"
                            : ""
                        }`}
                        style={{
                          outlineColor: selectedCandidate === cand.generation_id ? "var(--admin-accent, #4f46e5)" : "transparent",
                          cursor: "pointer",
                        }}
                      >
                        <span className="block text-xs ltr text-left font-mono" style={{ color: "var(--admin-text-primary)" }}>
                          {cand.generation_id}
                        </span>
                        <span className="block text-[11px] mt-1 ltr text-left font-bold" style={{ color: "var(--admin-accent, #4f46e5)" }}>
                          ⏱ {formatCandidateTime(cand)}
                        </span>
                        <span className="block text-[11px] mt-0.5 ltr text-left" style={{ color: "var(--admin-text-tertiary)" }}>
                          {asString(cand.model) ? `Model: ${cand.model}` : ""}
                          {asString(cand.api_key_name) ? ` · Key: ${cand.api_key_name}` : ""}
                          {asString(cand.api_type) ? ` · ${cand.api_type}` : ""}
                        </span>
                        <span className="block text-[11px] mt-0.5 ltr text-left" style={{ color: "var(--admin-text-tertiary)" }}>
                          P {formatNumber(asNumber(cand.tokens_prompt))} · C {formatNumber(asNumber(cand.tokens_completion))}
                          {asString(cand.total_usage) ? ` · $${cand.total_usage}` : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                    یا شناسه دیگری را وارد کنید:
                  </label>
                  <input
                    type="text"
                    className="admin-input ltr text-left"
                    placeholder="gen-1234567890"
                    dir="ltr"
                    value={genIdInput}
                    onChange={(e) => { setSelectedCandidate(null); setGenIdInput(e.target.value); }}
                  />
                </>
              ) : (
                <>
                  <p className="text-sm mb-3" style={{ color: "var(--admin-text-secondary)" }}>
                    این لاگ شناسه generation ندارد. شناسه <code className="ltr">gen-...</code> را از داشبورد OpenRouter (بخش Activity) کپی و اینجا جای‌گذاری کنید تا هزینه و وضعیت واقعی دریافت شود.
                  </p>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--admin-text-secondary)" }}>
                    شناسه OpenRouter Generation
                  </label>
                  <input
                    type="text"
                    className="admin-input ltr text-left"
                    placeholder="gen-1234567890"
                    dir="ltr"
                    value={genIdInput}
                    onChange={(e) => setGenIdInput(e.target.value)}
                  />
                </>
              )}
            </div>
            <div className="p-4 flex justify-end gap-2" style={{ borderTop: "1px solid var(--admin-border)" }}>
              {canRecover && (confirmDelete ? (
                <>
                  <span className="text-xs flex items-center ml-auto" style={{ color: "var(--admin-text-secondary)" }}>
                    حذف شود؟
                  </span>
                  <button
                    type="button"
                    className="admin-btn admin-btn-danger admin-btn-sm"
                    disabled={deleteMutation.isPending}
                    onClick={confirmDeleteLog}
                  >
                    بله، حذف کن
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary admin-btn-sm"
                    disabled={deleteMutation.isPending}
                    onClick={() => setConfirmDelete(false)}
                  >
                    انصراف
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="admin-btn admin-btn-danger admin-btn-sm ml-auto"
                  disabled={recoverMutation.isPending || deleteMutation.isPending || failMutation.isPending}
                  onClick={() => setConfirmDelete(true)}
                >
                  حذف لاگ
                </button>
              ))}
              {canRecover && (confirmFail ? (
                <>
                  <span className="text-xs flex items-center" style={{ color: "var(--admin-text-secondary)" }}>
                    وضعیت به ناموفق تغییر کند؟
                  </span>
                  <button
                    type="button"
                    className="admin-btn admin-btn-warning admin-btn-sm"
                    disabled={failMutation.isPending}
                    onClick={confirmFailLog}
                  >
                    بله، ناموفق کن
                  </button>
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary admin-btn-sm"
                    disabled={failMutation.isPending}
                    onClick={() => setConfirmFail(false)}
                  >
                    انصراف
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="admin-btn admin-btn-warning admin-btn-sm"
                  disabled={recoverMutation.isPending || deleteMutation.isPending || failMutation.isPending}
                  onClick={() => { setConfirmDelete(false); setConfirmFail(true); }}
                >
                  ناموفق کن
                </button>
              ))}
              <button
                type="button"
                className="admin-btn admin-btn-secondary admin-btn-sm"
                onClick={() => { setRecoverModalLog(null); setConfirmDelete(false); setConfirmFail(false); setSelectedCandidate(null); }}
              >
                انصراف
              </button>
              {canRecover && (
                <button
                  type="button"
                  className="admin-btn admin-btn-primary admin-btn-sm"
                  disabled={!genIdInput.trim() || recoverMutation.isPending}
                  onClick={() => { setConfirmDelete(false); confirmManualRecover(); }}
                >
                  {recoverMutation.isPending ? "در حال بازیابی…" : "بازیابی"}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}